import {
  Badge,
  Box,
  Flex,
  Skeleton,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import React from "react";
import ImagePreviewModal from "./ImagePreview";
import getAxios from "../hooks/useAxios";
import Cookies from "js-cookie";

interface ParcelCardProps {
  item: any;
}

const ParcelCard = ({ item }: ParcelCardProps) => {
  const axios = getAxios();
  const token = Cookies.get("x-admin-auth");

  const getStatusColor = (status: string) => {
    switch (status) {
      case "delivered":
        return "green";
      case "canceled":
        return "red";
      case "picked-up":
        return "blue";
      case "accepted":
        return "cyan";
      case "pending":
      case "searching":
        return "orange";
      default:
        return "gray";
    }
  };

  function arrayBufferToBase64(buffer: any) {
    let binary = "";
    let bytes = new Uint8Array(buffer);
    let len = bytes.length;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  const [image, setImage] = React.useState<string>();
  const [imageLoaded, setImageLoaded] = React.useState(false);

  React.useEffect(() => {
    if (!item?.photo_key) {
      setImageLoaded(true);
      return;
    }

    let cancelled = false;

    const getImageLink = async () => {
      setImageLoaded(false);

      try {
        const res = await axios.get(
          `uploads/${item.photo_key}?token=${token}`,
          {
            responseType: "arraybuffer",
          },
        );

        const base64String = arrayBufferToBase64(res.data);
        const dataUri = `data:image/jpeg;base64,${base64String}`;

        if (!cancelled) {
          setImage(dataUri);
        }
      } catch (error) {
        // console.error("Failed to load parcel image:", error);

        if (!cancelled) {
          setImage(undefined);
        }
      } finally {
        if (!cancelled) {
          setImageLoaded(true);
        }
      }
    };

    getImageLink();

    return () => {
      cancelled = true;
    };
  }, [item?.photo_key, token]);

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      padding="1.25rem"
      marginBottom="1rem"
      background={"white"}
    >
      {/* Header */}
      <Flex justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Text fontWeight="600">Parcel #{item.id}</Text>

          <Text fontSize="sm" color="gray.500">
            {item.created_at ? new Date(item.created_at).toLocaleString() : "-"}
          </Text>
        </Box>

        <Badge
          colorScheme={getStatusColor(item.status)}
          textTransform="capitalize"
        >
          {item.status || "-"}
        </Badge>
      </Flex>

      {/* Pickup / Drop */}
      <Flex gap={6} mb={4}>
        <Box flex={1}>
          <Text fontSize="sm" color="gray.500" mb={1}>
            Pickup
          </Text>

          <Text fontSize="sm">{item.origin?.place_name || "-"}</Text>
        </Box>

        <Box flex={1}>
          <Text fontSize="sm" color="gray.500" mb={1}>
            Drop
          </Text>

          <Text fontSize="sm">{item.destination?.place_name || "-"}</Text>
        </Box>
      </Flex>

      {/* Parcel Details */}
      <Flex gap={6} mb={5} flexWrap="wrap">
        <Box>
          <Text fontSize="xs" color="gray.500">
            Item Type
          </Text>

          <Text fontSize="sm" textTransform="capitalize">
            {item.item_type || "-"}
          </Text>
        </Box>

        <Box>
          <Text fontSize="xs" color="gray.500">
            Weight
          </Text>

          <Text fontSize="sm">
            {item.weight_kg ? `${item.weight_kg} kg` : "-"}
          </Text>
        </Box>

        <Box>
          <Text fontSize="xs" color="gray.500">
            Distance
          </Text>

          <Text fontSize="sm">
            {item.distance_km ? `${item.distance_km} km` : "-"}
          </Text>
        </Box>

        <Box>
          <Text fontSize="xs" color="gray.500">
            Fare
          </Text>

          <Text fontSize="sm" fontWeight="600">
            ₹{item.fare || 0}
          </Text>
        </Box>
      </Flex>
      <Box gap={6} mb={5} flexWrap="wrap">
        <Text fontSize="xs" color="gray.500">
          Parcel image
        </Text>
        {item?.photo_key ? (
          <ImageCard image={image} imageLoaded={imageLoaded} />
        ) : (
          <Text fontSize="xs">No parcel image uploaded</Text>
        )}
      </Box>

      {/* Receiver */}
      <Box borderTopWidth="1px" pt={4} mb={4}>
        <Text fontSize="sm" fontWeight="600" mb={2}>
          Receiver
        </Text>

        <Flex gap={8}>
          <Box>
            <Text fontSize="xs" color="gray.500">
              Name
            </Text>

            <Text fontSize="sm">{item.receiver_name || "-"}</Text>
          </Box>

          <Box>
            <Text fontSize="xs" color="gray.500">
              Mobile
            </Text>

            <Text fontSize="sm">{item.receiver_mobile || "-"}</Text>
          </Box>
        </Flex>
      </Box>

      {/* Rider & Driver */}
      <Flex borderTopWidth="1px" pt={4} gap={10}>
        {/* Rider */}
        <Box flex={1}>
          <Text fontSize="sm" fontWeight="600" mb={2}>
            Rider
          </Text>

          <Text fontSize="sm">{item.rider?.name || "-"}</Text>

          <Text fontSize="sm" color="gray.500">
            {item.rider?.mobile || "-"}
          </Text>
        </Box>

        {/* Driver */}
        <Box flex={1}>
          <Text fontSize="sm" fontWeight="600" mb={2}>
            Driver
          </Text>

          <Text fontSize="sm">{item.driver?.name || "-"}</Text>

          <Text fontSize="sm" color="gray.500">
            {item.driver?.mobile || "-"}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};

export default ParcelCard;

type ImageCardPropType = {
  imageLoaded: boolean;
  image: string | undefined;
};

function ImageCard({ imageLoaded, image }: ImageCardPropType) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <>
      <div
        style={{
          flex: "1",
          textAlign: "left",
        }}
      >
        <Skeleton isLoaded={imageLoaded} minH={"9.5rem"} borderRadius="8px">
          <img
            src={image}
            alt="No Image"
            onClick={onOpen}
            style={{
              cursor: "zoom-in",
              width: "20rem",
              aspectRatio: "16/9",
              borderRadius: "8px",
              border: "1px solid #ccc",
              objectFit: "contain",
            }}
          />
        </Skeleton>
      </div>
      <ImagePreviewModal isOpen={isOpen} onClose={onClose} image={image} />
    </>
  );
}
